import { classifyPlatformTenantAppearanceBase, platformTenantAppearancePresetCatalog, platformThemeTokensValueSchema, } from '../definitions/platform-setting-definitions.js';
const platformAppearancePresetIds = new Set([
    'crest-operational-classic-v1',
    'crest-neutral-v1',
    'field-high-contrast-v1',
    'water-pro-classic-v1',
    'water-pro-classic-v2',
    'custom-v1',
]);
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function deepMerge(base, override) {
    if (!isObject(base) || !isObject(override))
        return override;
    const merged = { ...base };
    for (const [key, value] of Object.entries(override)) {
        const existing = merged[key];
        merged[key] = existing === undefined ? value : deepMerge(existing, value);
    }
    return merged;
}
function schemaVersionsFromRegistryVersion(registryVersion) {
    if (registryVersion === undefined || registryVersion === null)
        return {};
    const prefix = 'crest-settings:';
    if (!registryVersion.startsWith(prefix))
        return {};
    const versions = {};
    for (const item of registryVersion.slice(prefix.length).split(',')) {
        const separator = item.lastIndexOf('@');
        if (separator < 1)
            continue;
        const version = Number(item.slice(separator + 1));
        if (Number.isInteger(version) && version > 0)
            versions[item.slice(0, separator)] = version;
    }
    return versions;
}
export class PlatformConfigurationRegistry {
    #registrations;
    constructor(registrations) {
        const entries = new Map();
        for (const registration of registrations) {
            const key = registration.definition.key;
            if (entries.has(key))
                throw new Error(`Duplicate configuration registration: ${key}`);
            entries.set(key, registration);
        }
        this.#registrations = entries;
    }
    registrations() {
        return [...this.#registrations.values()];
    }
    registration(key) {
        const registration = this.#registrations.get(key);
        if (registration === undefined)
            throw new Error(`Unknown configuration key: ${key}`);
        return registration;
    }
    registryVersion() {
        const settingVersions = this.registrations()
            .map(({ definition }) => `${definition.key}@${definition.schemaVersion}`)
            .sort();
        return `crest-settings:${settingVersions.join(',')}`;
    }
    schemaVersions() {
        return Object.freeze(Object.fromEntries(this.registrations()
            .map(({ definition }) => [definition.key, definition.schemaVersion])
            .sort(([first], [second]) => (first < second ? -1 : first > second ? 1 : 0))));
    }
    schemaVersionsForRelease(storedSchemaVersions = {}, storedRegistryVersion) {
        return Object.freeze({
            ...schemaVersionsFromRegistryVersion(storedRegistryVersion),
            ...storedSchemaVersions,
            ...this.schemaVersions(),
        });
    }
    valuesForRelease(storedValues, resolvedInstalledValues) {
        return Object.freeze({ ...storedValues, ...resolvedInstalledValues });
    }
    codedFallbacks() {
        return Object.fromEntries(this.registrations().map(({ definition }) => [definition.key, definition.codedFallback]));
    }
    clientSafeValues(values, allowedModuleIds) {
        return Object.freeze(Object.fromEntries(this.registrations()
            .filter(({ definition }) => definition.clientSafe &&
            !definition.serverOnly &&
            !definition.sensitive &&
            (allowedModuleIds === undefined || allowedModuleIds.has(definition.moduleId)))
            .map(({ definition }) => [definition.key, values[definition.key]])));
    }
    managedRegistrations(allowedModuleIds) {
        return this.registrations().filter(({ definition }) => definition.tenantEditable &&
            !definition.sensitive &&
            (definition.clientSafe || definition.serverOnly) &&
            (allowedModuleIds === undefined || allowedModuleIds.has(definition.moduleId)));
    }
    managedValues(values, allowedModuleIds) {
        return Object.freeze(Object.fromEntries(this.managedRegistrations(allowedModuleIds)
            .map(({ definition }) => [definition.key, values[definition.key]])));
    }
    managedSchemaVersions(allowedModuleIds) {
        return Object.freeze(Object.fromEntries(this.managedRegistrations(allowedModuleIds)
            .map(({ definition }) => [definition.key, definition.schemaVersion])
            .sort(([first], [second]) => (first < second ? -1 : first > second ? 1 : 0))));
    }
    managedRegistryVersion(allowedModuleIds) {
        return `crest-managed-settings:${Object.entries(this.managedSchemaVersions(allowedModuleIds))
            .map(([key, version]) => `${key}@${version}`)
            .join(',')}`;
    }
    clientSafeSchemaVersions(allowedModuleIds) {
        return Object.freeze(Object.fromEntries(this.registrations()
            .filter(({ definition }) => definition.clientSafe &&
            !definition.serverOnly &&
            !definition.sensitive &&
            (allowedModuleIds === undefined || allowedModuleIds.has(definition.moduleId)))
            .map(({ definition }) => [definition.key, definition.schemaVersion])
            .sort(([first], [second]) => (first < second ? -1 : first > second ? 1 : 0))));
    }
    clientSafeRegistryVersion(allowedModuleIds) {
        return `crest-client-settings:${Object.entries(this.clientSafeSchemaVersions(allowedModuleIds))
            .map(([key, version]) => `${key}@${version}`)
            .join(',')}`;
    }
    parseValue(key, value) {
        if (key === 'platform.theme.tokens') {
            return platformThemeTokensValueSchema.parse(value);
        }
        const registration = this.registration(key);
        return registration.valueSchema.parse(value);
    }
    migrateValue(key, fromVersion, value) {
        const registration = this.registration(key);
        const targetVersion = registration.definition.schemaVersion;
        if (!Number.isInteger(fromVersion) || fromVersion < 1 || fromVersion > targetVersion) {
            throw new Error(`Unsupported schema version ${fromVersion} for ${key}.`);
        }
        let migrated = value;
        for (let version = fromVersion; version < targetVersion; version += 1) {
            const migration = registration.migrations.find((candidate) => candidate.fromVersion === version && candidate.toVersion === version + 1);
            if (migration === undefined) {
                throw new Error(`Missing ${key} migration from schema version ${version}.`);
            }
            migrated = migration.migrate(migrated);
        }
        if (registration.definition.mergeStrategy === 'deep-merge') {
            migrated = deepMerge(key === 'platform.theme.tokens'
                ? platformTenantAppearancePresetCatalog['crest-neutral-v1'].theme
                : registration.definition.codedFallback, migrated);
        }
        return this.parseValue(key, migrated);
    }
    resolveStoredValues(values, schemaVersions = {}, registryVersion) {
        const legacySchemaVersions = schemaVersionsFromRegistryVersion(registryVersion);
        const migrated = {};
        let storedThemeLineage;
        for (const registration of this.registrations()) {
            const key = registration.definition.key;
            const value = values[key];
            if (value === undefined)
                continue;
            const fromVersion = schemaVersions[key] ?? legacySchemaVersions[key] ?? registration.definition.schemaVersion;
            migrated[key] = fromVersion === registration.definition.schemaVersion
                ? value
                : this.migrateValue(key, fromVersion, value);
            if (key === 'platform.theme.tokens' &&
                fromVersion === registration.definition.schemaVersion &&
                isObject(value) &&
                typeof value.presetId === 'string' &&
                platformAppearancePresetIds.has(value.presetId) &&
                typeof value.presetChecksum === 'string') {
                storedThemeLineage = {
                    presetId: value.presetId,
                    presetChecksum: value.presetChecksum,
                };
            }
        }
        const valuesForResolution = storedThemeLineage === undefined
            ? migrated
            : {
                ...migrated,
                'platform.theme.tokens': {
                    ...migrated['platform.theme.tokens'],
                    presetId: 'custom-v1',
                },
            };
        const resolved = this.resolve({ tenant: valuesForResolution });
        if (storedThemeLineage === undefined)
            return resolved;
        const theme = resolved['platform.theme.tokens'];
        if (theme === undefined || !isObject(theme))
            return resolved;
        return Object.freeze({
            ...resolved,
            'platform.theme.tokens': Object.freeze({
                ...theme,
                ...storedThemeLineage,
            }),
        });
    }
    resolve(input) {
        const resolved = {};
        for (const registration of this.registrations()) {
            const { definition } = registration;
            let value = definition.key === 'platform.theme.tokens'
                ? platformTenantAppearancePresetCatalog['crest-neutral-v1'].theme
                : definition.codedFallback;
            for (const scope of ['platform', 'plan', 'tenant']) {
                const candidate = input[scope]?.[definition.key];
                if (candidate === undefined || !definition.permittedScopes.includes(scope))
                    continue;
                value =
                    definition.mergeStrategy === 'deep-merge' ? deepMerge(value, candidate) : candidate;
            }
            resolved[definition.key] = this.parseValue(definition.key, value);
        }
        const theme = resolved['platform.theme.tokens'];
        const layout = resolved['platform.layout.shell'];
        const accessibility = resolved['platform.accessibility.preferences'];
        if (theme !== undefined && layout !== undefined && accessibility !== undefined) {
            const lineage = classifyPlatformTenantAppearanceBase({
                'platform.theme.tokens': theme,
                'platform.layout.shell': layout,
                'platform.accessibility.preferences': accessibility,
            });
            resolved['platform.theme.tokens'] = this.parseValue('platform.theme.tokens', {
                ...theme,
                ...lineage,
            });
        }
        for (const registration of this.registrations()) {
            const { definition } = registration;
            const resolvedValue = resolved[definition.key];
            if (resolvedValue === undefined)
                continue;
            let value = resolvedValue;
            for (const scope of ['user', 'device']) {
                const candidate = input[scope]?.[definition.key];
                if (candidate === undefined || !definition.permittedScopes.includes(scope))
                    continue;
                value = definition.mergeStrategy === 'deep-merge' ? deepMerge(value, candidate) : candidate;
            }
            resolved[definition.key] = this.parseValue(definition.key, value);
        }
        return Object.freeze(resolved);
    }
}
//# sourceMappingURL=platform-configuration-registry.js.map